import { isNotNull } from '@grupoboticario/barril-del-chavo'
import { IProduct } from 'src/@interfaces'
import * as yup from 'yup'

export function objHasComponentsProp(obj: unknown): obj is { components: unknown[] } {
  return (
    typeof obj === 'object' &&
    obj != null &&
    'components' in obj &&
    Array.isArray((obj as any).components)
  )
}

export function objHasComponentProp(obj: unknown): obj is { component: string } {
  return (
    typeof obj === 'object' &&
    obj != null &&
    'component' in obj &&
    typeof (obj as any).component === 'string'
  )
}

export function getDynamicShowcaseSchema(componentName: string) {
  return yup.object({
    component: yup.string().oneOf([componentName]).required(),
    options: yup
      .object({
        title: yup.string().required(),
        skus: yup
          .array()
          .of(yup.string().required())
          .required()
          .transform((_, originalValue) => {
            try {
              let skus: string[] = []
              if (Array.isArray(originalValue)) {
                skus = originalValue
              } else if (typeof originalValue === 'string') {
                skus = originalValue.split(',').map((s) => s.trim())
              }

              return Array.from(
                new Set(skus.filter((sku) => typeof sku === 'string' && /^\d+$/.test(sku))),
              )
            } catch (error) {
              return []
            }
          }),
        linkSeeAll: yup
          .object({
            linkType: yup.string().required(),
            key: yup.string().optional(),
            url: yup.string().optional(),
          })
          .optional(),
        bannerDesktop: yup
          .object({
            alt: yup.string().nullable().optional(),
            url: yup.string().nullable().optional(),
          })
          .nullable()
          .optional(),
        bannerMobile: yup
          .object({
            alt: yup.string().nullable().optional(),
            url: yup.string().nullable().optional(),
          })
          .nullable()
          .optional(),
        action: yup.object().optional(),
      })
      .required(),
  })
}

export type DynamicShowcaseSchema = ReturnType<typeof getDynamicShowcaseSchema>

export type DynamicShowcase = yup.InferType<DynamicShowcaseSchema>

export type ProductEnhancedDynamicShowcase = DynamicShowcase & { products: IProduct[] }

class TransformToShowcaseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TransformToShowcaseError'
  }
}

export function transformToShowcase(showcasesComponentName: string) {
  try {
    const dynamicShowcaseSchema = getDynamicShowcaseSchema(showcasesComponentName)

    return (dynamicComponent: unknown): DynamicShowcase | null => {
      try {
        const parsed = dynamicShowcaseSchema.validateSync(dynamicComponent, { stripUnknown: false })

        return {
          ...parsed,
          options: {
            ...parsed.options,
            skus: (Array.isArray(parsed.options.skus)
              ? parsed.options.skus
              : [parsed.options.skus]
            ).map(String),
          },
        }
      } catch (e) {
        if (e instanceof yup.ValidationError) {
          window.NREUM.noticeError(
            new TransformToShowcaseError('Failed to transform dynamic component to showcase'),
            {
              customAttributes: {
                component: showcasesComponentName,
                error: e.message,
                originalValue: JSON.stringify(dynamicComponent),
              },
              customError: true,
            },
          )
        }

        return null
      }
    }
  } catch (error) {
    return () => null
  }
}

export function transformToShowcases(
  showcasesComponentName: string,
  dynamicComponents: unknown[],
): DynamicShowcase[] {
  return dynamicComponents
    .filter((c) => objHasComponentProp(c) && c.component === showcasesComponentName)
    .map(transformToShowcase(showcasesComponentName))
    .filter(isNotNull)
}

export function getShowcasesFromDynamicContent(
  dynamicContent: unknown,
  showcaseComponentName: string,
): DynamicShowcase[] {
  if (
    !objHasComponentsProp(dynamicContent) ||
    !dynamicContent?.components ||
    !Array.isArray(dynamicContent.components)
  ) {
    return []
  }

  return transformToShowcases(showcaseComponentName, dynamicContent.components)
}
